import { getServiceClient } from '@api/client-factory/StoreManagementClientFactory'

import {
    BatchGetStoreConfigurationsInput,
    BatchGetStoreConfigurationsOutput,
    GetStoreConfigurationsInput,
    GetStoreConfigurationsOutput,
    UpdateStoreConfigurationInput,
    UpdateStoreConfigurationOutput,
} from '@amzn/coral_com-amazon-jwo-store-configurations'
import { validateRequired, validateRequiredArray } from './listenerUtils'

// ========================================
// READ OPERATIONS (GetStoreConfigurations)
// ========================================

/**
 * Get all Keystone configurations for a store.
 * Required: storeId
 */
export async function getStoreConfigurationsListener (message: any): Promise<GetStoreConfigurationsOutput> {
    const params = (message && message.data) || {}
    validateRequired(params, 'storeId')
    const client = getServiceClient()
    return client.getStoreConfigurations(GetStoreConfigurationsInput.clone(params)).toPromise()
}

// ========================================
// WRITE OPERATIONS (per-feature updates)
// ========================================

/**
 * Update payment processor configuration.
 * Required: storeId, data
 */
export async function updatePaymentProcessorConfigListener (message: any): Promise<UpdateStoreConfigurationOutput> {
    const params = (message && message.data) || {}
    validateRequired(params, 'storeId', 'data')
    const client = getServiceClient()
    return client.updatePaymentProcessorConfig(UpdateStoreConfigurationInput.clone(params)).toPromise()
}

/**
 * Update pre-auth configuration.
 * Required: storeId, data
 */
export async function updatePreAuthConfigListener (message: any): Promise<UpdateStoreConfigurationOutput> {
    const params = (message && message.data) || {}
    validateRequired(params, 'storeId', 'data')
    const client = getServiceClient()
    return client.updatePreAuthConfig(UpdateStoreConfigurationInput.clone(params)).toPromise()
}

/**
 * Update entry QR configuration.
 * Required: storeId, data
 */
export async function updateEntryQRConfigListener (message: any): Promise<UpdateStoreConfigurationOutput> {
    const params = (message && message.data) || {}
    validateRequired(params, 'storeId', 'data')
    const client = getServiceClient()
    return client.updateEntryQRConfig(UpdateStoreConfigurationInput.clone(params)).toPromise()
}

/**
 * Update connector endpoints (identity/ordering/loyalty).
 * Required: storeId, data (key-wrapped: {"identity_connector": {...}})
 */
export async function updateConnectorEndpointsListener (message: any): Promise<UpdateStoreConfigurationOutput> {
    const params = (message && message.data) || {}
    validateRequired(params, 'storeId', 'data')
    const client = getServiceClient()
    return client.updateConnectorEndpoints(UpdateStoreConfigurationInput.clone(params)).toPromise()
}

/**
 * Update connector AWS accounts.
 * Required: storeId, data
 */
export async function updateConnectorAwsAccountsListener (message: any): Promise<UpdateStoreConfigurationOutput> {
    const params = (message && message.data) || {}
    validateRequired(params, 'storeId', 'data')
    const client = getServiceClient()
    return client.updateConnectorAwsAccounts(UpdateStoreConfigurationInput.clone(params)).toPromise()
}

/**
 * Update receipt settings.
 * Required: storeId, data
 */
export async function updateReceiptSettingsListener (message: any): Promise<UpdateStoreConfigurationOutput> {
    const params = (message && message.data) || {}
    validateRequired(params, 'storeId', 'data')
    const client = getServiceClient()
    return client.updateReceiptSettings(UpdateStoreConfigurationInput.clone(params)).toPromise()
}

/**
 * Update fraud-and-abuse (FnA) strategy configuration.
 * Required: storeId, data
 */
export async function updateFnAConfigListener (message: any): Promise<UpdateStoreConfigurationOutput> {
    const params = (message && message.data) || {}
    validateRequired(params, 'storeId', 'data')
    const client = getServiceClient()
    return client.updateFnAConfig(UpdateStoreConfigurationInput.clone(params)).toPromise()
}

// ========================================
// BATCH READ OPERATIONS
// ========================================

/**
 * Batch get configurations for multiple stores (up to 30).
 * Required: storeIds
 */
export async function batchGetStoreConfigurationsListener (message: any): Promise<BatchGetStoreConfigurationsOutput> {
    const params = (message && message.data) || {}
    validateRequiredArray(params, 'storeIds')
    if (!Array.isArray(params.storeIds)) {
        throw new Error('storeIds must be an array')
    }
    if (params.storeIds.length > 30) {
        throw new Error('storeIds exceeds the maximum batch size of 30')
    }
    const client = getServiceClient()
    return client.batchGetStoreConfigurations(BatchGetStoreConfigurationsInput.clone(params)).toPromise()
}
